import React from "react";
import { isEmpty } from "lodash";
import { ReactSVG } from "react-svg";
import { PreviewLayout } from "./";
import { Typography } from "@components/Typography";
import { IconList } from "@components/IconList";
import { Button } from "@components/Button";
import { Lazy } from "@/components/Lazy";
import { Icons } from "@/types/icons";
import { generateVCard } from "@/utils/downloadHelper";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { getFullAddress } from "@/utils/qrEditorHelper";
import { optimizeCloudinaryImage } from "@/services/CloudinaryService";



export default function PreviewVcard(props: QRProps<"vcard">) {
  const t = props.previewTranslations;
  const qrEditorContextData = React.useContext<QREditorContextProps<"vcard">>(QREditorContext);
  const isInitPreview = props?.initPreview;
  const data = props?.data || qrEditorContextData?.qrData.data;

  const { address, numeration, zipCode, city, state, country, themeColor } = data;

  const fullAddress = !isInitPreview
    ? getFullAddress(address, numeration, city, zipCode, state, country)
    : data.address;

  const addressMapsURL = `http://maps.google.com/?q=${fullAddress}`;

  const vcardFileData = {
    logoUrl: data?.logoUrl,
    fullName: data?.fullName,
    email: data?.email,
    mobilePhone: data?.mobilePhone,
    landlinePhone: data?.landlinePhone,
    fax: data?.fax,
    website: data?.website,
    companyName: data?.companyName,
    profession: data?.profession,
    address: fullAddress,
  };

  return (
    <PreviewLayout
      page="vcard"
      dataQA="preview-vcard-container"
      backgroundColor={"white"}
      includeBrowser
      initPreview={isInitPreview}>
      <div
        className="top-section"
        style={{ background: data?.themeColor?.backgroundColor }}>
        {data?.logoUrl && (
          <div className="logoUrl--container">
            <Lazy placeholderClass="logoUrl--background">
              <img
                src={optimizeCloudinaryImage({
                  url: data?.logoUrl,
                  transformations: ["w_400,h_400,q_90,f_auto,c_fill"],
                })}
                alt="vcard avatar"
                className="logoUrl--background"
              />
            </Lazy>
          </div>
        )}
        {data?.fullName && (
          <div className="vcard-title">
            <Typography
              Tag="p"
              size="l"
              classname="u-semibold u-spacing-4">
              {data?.fullName}
            </Typography>
          </div>
        )}

        {data?.profession && (
          <div className="vcard-subtitle ">
            <Typography
              Tag="p"
              classname="u-spacing-5">
              {data?.profession}
            </Typography>
          </div>
        )}
      </div>
      {data?.fullName && (
        <div className="u-line contact-button">
          <Button
            to=""
            dataQA="preview-vcard-add-button"
            iconLeft
            icon={
              <ReactSVG
                className="button__icon"
                src={`/svg/icons/${Icons.addContact}.svg`}
              />
            }
            spacing={2}
            label={"Add to contacts"}
            buttonColor={themeColor?.buttonColor}
            onClick={() => generateVCard(vcardFileData)}
          />
        </div>
      )}

      <div className="qr-content">
        <div>
          {data?.email && (
            <IconList
              iconItem
              items={{
                icon: Icons.email,
                title: isInitPreview ? "Email" : t && t.vcardPreviewEmail,
                link: `mailto:${data?.email}`,
                subtitle: data?.email,
              }}
              uncheckValidURL
            />
          )}
          {data?.mobilePhone && (
            <IconList
              iconItem
              items={{
                icon: Icons.mobilePhone,
                title: isInitPreview ? "Mobile Phone" : t && t.vcardPreviewPhone,
                link: `tel:${data?.mobilePhone}`,
                subtitle: data?.mobilePhone,
              }}
              uncheckValidURL
            />
          )}
          {data?.landlinePhone && (
            <IconList
              iconItem
              items={{
                icon: Icons.landlinePhone,
                title: t && t.vcardPreviewPhoneAlt,
                link: `tel:${data?.landlinePhone}`,
                subtitle: data?.landlinePhone,
              }}
              uncheckValidURL
            />
          )}
          {data?.fax && (
            <IconList
              iconItem
              items={{
                icon: Icons.fax,
                title: t && t.vcardPreviewFaxPhone,
                subtitle: data?.fax,
                link: `fax:${data?.fax}`,
              }}
              uncheckValidURL
            />
          )}
          {address && (
            <IconList
              iconItem
              items={{
                icon: Icons.location,
                subtitle: fullAddress,
                link: addressMapsURL,
              }}
            />
          )}
          {data?.website && (
            <IconList
              iconItem
              items={{
                icon: Icons.website,
                title: isInitPreview ? "Website" : t && t.vcardPreviewWebsite,
                subtitle: data?.website,
                link: data?.website,
              }}
            />
          )}
          {data?.companyName && (
            <IconList
              iconItem
              items={{
                icon: Icons.building,
                title: t && t.vcardPreviewCompany,
                subtitle: data?.companyName,
              }}
            />
          )}
          {data?.profession && !isInitPreview && (
            <IconList
              iconItem
              items={{
                icon: Icons.profession,
                title: t && t.vcardPreviewProfession,
                subtitle: data?.profession,
              }}
            />
          )}
        </div>
      </div>
      {data?.summary && <div className="summary__container">{data?.summary}</div>}
      {!isEmpty(data?.socialNetworks) && (
        <div className="qr-content">
          <div className="business-socialNetworks">
            <IconList
              center
              items={data?.socialNetworks}
            />
          </div>
        </div>
      )}
    </PreviewLayout>
  );
}
